import React, { useEffect, useState, useRef } from "react";
import Cookies from "js-cookie";
import {
  Alert,
  Autocomplete,
  Box,
  Button,

  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import EditIcon from "@mui/icons-material/Edit";
import { DndContext, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { uploadImage } from "../utils/uploadImage";
import { RECIPE_TYPES } from "../constants/Constants";
import { refreshRecipes } from "../utils/refreshRecipes";
import { SortableIngredientRow, SortableDirectionRow, SortableSection, SectionReorderModal } from "./SortableRows";
import SwapVertIcon from "@mui/icons-material/SwapVert";

const EMPTY_ING_CAT = { name: "", ingredients: [{ ingredient: "", quantity: "", unit: "" }] };
const EMPTY_DIR_CAT = { name: "", directions: [""] };

function groupIngredientsToCategories(ingredients = []) {
  const map = {};
  ingredients.forEach((ing) => {
    const cat = ing.category_name || "";
    if (!map[cat]) map[cat] = [];
    map[cat].push({ ingredient: ing.ingredient || "", quantity: ing.quantity || "", unit: ing.unit || "" });
  });
  return Object.keys(map).length
    ? Object.keys(map).map((k) => ({ name: k, ingredients: map[k] }))
    : [{ ...EMPTY_ING_CAT }];
}

function groupDirectionsToCategories(directions = []) {
  const map = {};
  directions
    .slice()
    .sort((a, b) => (a.direction_order || 0) - (b.direction_order || 0))
    .forEach((d) => {
      const cat = d.category_name || "";
      if (!map[cat]) map[cat] = [];
      map[cat].push(d.direction || "");
    });
  return Object.keys(map).length
    ? Object.keys(map).map((k) => ({ name: k, directions: map[k] }))
    : [{ ...EMPTY_DIR_CAT }];
}

const RecipeEdit = ({ open, onClose, recipe }) => {
  const previewUrlRef = useRef(null);

  const setUploadedImage = (file) => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = file ? URL.createObjectURL(file) : null;
    setForm((f) => ({ ...f, uploaded_image: file }));
  };

  const [form, setForm] = useState({
    title: "",
    authors: [],
    prep_time: "",
    recipe_yield: "",
    recipe_description: "",
    recipe_type_id: "",
    uploaded_image: "",
    ingredient_categories: [{ ...EMPTY_ING_CAT }],
    direction_categories: [{ ...EMPTY_DIR_CAT }],
  });
  const [authorOptions, setAuthorOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [crossSectionToast, setCrossSectionToast] = useState(false);
  const [reorderModal, setReorderModal] = useState(null);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const handleIngredientDragEnd = (catIndex, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const [acat] = active.id.split('-');
    const [ocat] = over.id.split('-');
    if (acat !== ocat) { setCrossSectionToast(true); return; }
    const cat = form.ingredient_categories[catIndex];
    const oldIdx = cat.ingredients.findIndex((_, i) => `${catIndex}-ing-${i}` === active.id);
    const newIdx = cat.ingredients.findIndex((_, i) => `${catIndex}-ing-${i}` === over.id);
    const updated = form.ingredient_categories.map((c, ci) =>
      ci === catIndex ? { ...c, ingredients: arrayMove(c.ingredients, oldIdx, newIdx) } : c
    );
    setForm({ ...form, ingredient_categories: updated });
  };

  const handleIngredientCategoryDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = form.ingredient_categories.findIndex((_, i) => `ing-cat-${i}` === active.id);
    const newIdx = form.ingredient_categories.findIndex((_, i) => `ing-cat-${i}` === over.id);
    setForm({ ...form, ingredient_categories: arrayMove(form.ingredient_categories, oldIdx, newIdx) });
  };

  const handleDirectionCategoryDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = form.direction_categories.findIndex((_, i) => `dir-cat-${i}` === active.id);
    const newIdx = form.direction_categories.findIndex((_, i) => `dir-cat-${i}` === over.id);
    setForm({ ...form, direction_categories: arrayMove(form.direction_categories, oldIdx, newIdx) });
  };

  const handleDirectionDragEnd = (catIndex, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const [acat] = active.id.split('-');
    const [ocat] = over.id.split('-');
    if (acat !== ocat) { setCrossSectionToast(true); return; }
    const cat = form.direction_categories[catIndex];
    const oldIdx = cat.directions.findIndex((_, i) => `${catIndex}-dir-${i}` === active.id);
    const newIdx = cat.directions.findIndex((_, i) => `${catIndex}-dir-${i}` === over.id);
    const updated = form.direction_categories.map((c, ci) =>
      ci === catIndex ? { ...c, directions: arrayMove(c.directions, oldIdx, newIdx) } : c
    );
    setForm({ ...form, direction_categories: updated });
  };

  useEffect(() => {
    if (recipe) {
      setForm({
        title: recipe.title || "",
        authors: recipe.authors?.length > 0
          ? recipe.authors
          : recipe.author ? [{ display_name: recipe.author, author_id: recipe.author_id || null, author_user_id: recipe.author_user_id || null, order: 0 }]
          : [],
        prep_time: recipe.prep_time ? String(recipe.prep_time) : "",
        recipe_yield: recipe.recipe_yield || "",
        recipe_description: recipe.recipe_description || "",
        recipe_type_id: recipe.recipe_type_id || "",
        uploaded_image: "",
        ingredient_categories: groupIngredientsToCategories(recipe.ingredients || []),
        direction_categories: groupDirectionsToCategories(recipe.directions || []),
      });
      setErrors({});
      setServerError(null);
    }
  }, [recipe, open]);

  useEffect(() => {
    const headers = { Authorization: "Bearer " + Cookies.get("token") };
    Promise.all([
      fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/authors`, { headers }).then(r => r.json()),
      fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/users`, { headers }).then(r => r.json()),
    ]).then(([authors, usersData]) => {
      const authorOpts = (authors || []).map(a => ({ id: a.id, name: a.name, type: 'author' }));
      const userOpts = (usersData.users || []).map(u => ({
        id: u.id,
        name: u.display_name || `${u.first_name} ${u.last_name}`.trim(),
        type: 'user',
      }));
      setAuthorOptions([...authorOpts, ...userOpts]);
    }).catch(() => {});
  }, []);

  // ── Ingredient helpers ────────────────────────────────────────────────────

  const handleCategoryNameChange = (value, type, catIndex) => {
    const key = type === "ingredients" ? "ingredient_categories" : "direction_categories";
    const updated = form[key].map((cat, i) => i === catIndex ? { ...cat, name: value } : cat);
    setForm({ ...form, [key]: updated });
  };

  const handleIngredientChange = (value, field, catIndex, index) => {
    const updated = form.ingredient_categories.map((cat, ci) => {
      if (ci !== catIndex) return cat;
      const ingredients = cat.ingredients.map((ing, ii) => ii === index ? { ...ing, [field]: value } : ing);
      return { ...cat, ingredients };
    });
    setForm({ ...form, ingredient_categories: updated });
  };

  const handleDirectionChange = (value, catIndex, index) => {
    const updated = form.direction_categories.map((cat, ci) => {
      if (ci !== catIndex) return cat;
      const directions = cat.directions.map((d, di) => di === index ? value : d);
      return { ...cat, directions };
    });
    setForm({ ...form, direction_categories: updated });
  };

  const addIngredient = (catIndex) => {
    const updated = form.ingredient_categories.map((cat, ci) =>
      ci === catIndex ? { ...cat, ingredients: [...cat.ingredients, { ingredient: "", quantity: "", unit: "" }] } : cat
    );
    setForm({ ...form, ingredient_categories: updated });
  };

  const addDirection = (catIndex) => {
    const updated = form.direction_categories.map((cat, ci) =>
      ci === catIndex ? { ...cat, directions: [...cat.directions, ""] } : cat
    );
    setForm({ ...form, direction_categories: updated });
  };

  const addIngredientCategory = () => setForm({
    ...form,
    ingredient_categories: [...form.ingredient_categories, { name: "", ingredients: [{ ingredient: "", quantity: "", unit: "" }] }],
  });

  const addDirectionCategory = () => setForm({
    ...form,
    direction_categories: [...form.direction_categories, { name: "", directions: [""] }],
  });

  const deleteIngredient = (catIndex, index) => {
    const updated = form.ingredient_categories.map((cat, ci) =>
      ci === catIndex ? { ...cat, ingredients: cat.ingredients.filter((_, ii) => ii !== index) } : cat
    );
    setForm({ ...form, ingredient_categories: updated });
  };

  const deleteDirection = (catIndex, index) => {
    const updated = form.direction_categories.map((cat, ci) =>
      ci === catIndex ? { ...cat, directions: cat.directions.filter((_, di) => di !== index) } : cat
    );
    setForm({ ...form, direction_categories: updated });
  };

  const deleteIngredientCategory = (catIndex) => setForm({
    ...form,
    ingredient_categories: form.ingredient_categories.filter((_, i) => i !== catIndex),
  });

  const deleteDirectionCategory = (catIndex) => setForm({
    ...form,
    direction_categories: form.direction_categories.filter((_, i) => i !== catIndex),
  });

  // ── Validation & submit ───────────────────────────────────────────────────

  const validate = () => {
    const newErrors = {};
    if (!form.authors || form.authors.length === 0) newErrors.author = "At least one author is required";
    if (!form.recipe_type_id) newErrors.recipe_type = "Please select a Recipe Type";
    if (!form.ingredient_categories.some((cat) => (cat.ingredients || []).some((ing) => ing.ingredient?.trim())))
      newErrors.ingredients = "At least one ingredient is required";
    if (!form.direction_categories.some((cat) => (cat.directions || []).some((d) => d?.trim())))
      newErrors.directions = "At least one direction is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = async () => {
    const payload = {
      title: form.title,
      author: form.authors[0]?.display_name || null,
      author_id: form.authors[0]?.author_id || null,
      author_user_id: form.authors[0]?.author_user_id || null,
      authors: form.authors.map((a, i) => ({ ...a, order: i })),
      recipe_type_id: form.recipe_type_id ? parseInt(form.recipe_type_id, 10) : null,
      prep_time: form.prep_time || null,
      recipe_yield: form.recipe_yield || null,
      recipe_description: form.recipe_description || null,
      ingredients: form.ingredient_categories.flatMap((cat, catIndex) =>
        (cat.ingredients || [])
          .filter((ing) => ing?.ingredient?.trim())
          .map((ing) => ({
            ingredient: ing.ingredient,
            quantity: ing.quantity || null,
            unit: ing.unit || null,
            category_name: cat.name || null,
            category_order: catIndex,
          }))
      ),
      directions: (() => {
        const dirs = [];
        let order = 1;
        for (const [catIndex, cat] of form.direction_categories.entries()) {
          for (const d of (cat.directions || []).filter((dd) => dd?.trim())) {
            dirs.push({ direction: d, direction_order: order++, category_name: cat.name || null, category_order: catIndex });
          }
        }
        return dirs;
      })(),
    };

    if (form.uploaded_image) {
      await new Promise((resolve, reject) => {
        uploadImage(
          form.uploaded_image,
          (error) => reject(error),
          (imageData) => {
            payload.image_url = imageData[0];
            payload.download_image_url = imageData[1];
            resolve();
          }
        );
      });
    }

    return payload;
  };

  const handleSave = async () => {
    setServerError(null);
    setErrors({});
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = await buildPayload();
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/update/${recipe.id}`, {
        method: "PUT",
        headers: {
          Authorization: "Bearer " + Cookies.get("token"),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        setServerError(body?.detail ? `Update failed: ${body.detail}` : "Update failed. Please try again.");
        return;
      }

      const data = await resp.json();
      setShowSuccess(true);

      const storedRecipes = JSON.parse(sessionStorage.getItem("recipes")) || { recipes: {} };
      if (storedRecipes.recipes?.[recipe.id]) {
        storedRecipes.recipes[recipe.id] = { recipe: data.recipe };
        sessionStorage.setItem("recipes", JSON.stringify(storedRecipes));
      }
      refreshRecipes(true);

      setTimeout(() => {
        setShowSuccess(false);
        onClose?.(data.recipe);
      }, 600);
    } catch (err) {
      console.error("Recipe update error:", err);
      setServerError(`Unexpected error: ${err?.message || err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    setServerError(null);
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    onClose?.();
  };

  return (
    <Dialog open={!!open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><EditIcon sx={{ color: 'primary.main' }} />Edit Recipe</DialogTitle>
      <DialogContent>
        <Snackbar open={showSuccess} autoHideDuration={6000} onClose={() => setShowSuccess(false)}>
          <Alert severity="success" variant="filled" sx={{ width: "100%" }} onClose={() => setShowSuccess(false)}>
            Recipe updated!
          </Alert>
        </Snackbar>
        <Snackbar open={crossSectionToast} autoHideDuration={3000} onClose={() => setCrossSectionToast(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity="info" variant="filled" onClose={() => setCrossSectionToast(false)}>
            Reordering between sections isn't supported — drag within a section only.
          </Alert>
        </Snackbar>
        <SectionReorderModal
          open={reorderModal === 'ingredients'}
          onClose={() => setReorderModal(null)}
          sections={form.ingredient_categories}
          title="Ingredient"
          onApply={(order) => setForm({ ...form, ingredient_categories: order.map(i => form.ingredient_categories[i]) })}
        />
        <SectionReorderModal
          open={reorderModal === 'directions'}
          onClose={() => setReorderModal(null)}
          sections={form.direction_categories}
          title="Direction"
          onApply={(order) => setForm({ ...form, direction_categories: order.map(i => form.direction_categories[i]) })}
        />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {/* Title (read-only) */}
          <TextField label="Recipe Name" variant="outlined" fullWidth value={form.title} disabled />

          <Autocomplete
            multiple
            freeSolo
            options={authorOptions.filter(o => !form.authors.some(a => (a.author_id === o.id && o.type === 'author') || (a.author_user_id === o.id && o.type === 'user')))}
            groupBy={(o) => typeof o === 'object' && o.type !== 'free' ? (o.type === 'author' ? 'Author Profiles' : 'Family Members') : undefined}
            getOptionLabel={(o) => typeof o === 'string' ? o : o.name}
            isOptionEqualToValue={(o, v) => {
              if (typeof o === 'string' || typeof v === 'string') return o === v;
              if (o.type === 'free' || v.type === 'free') return o.name === v.name;
              return o.id === v.id && o.type === v.type;
            }}
            value={form.authors.map(a => {
              if (a.author_id) return authorOptions.find(o => o.id === a.author_id && o.type === 'author') ?? { id: a.author_id, name: a.display_name, type: 'author' };
              if (a.author_user_id) return authorOptions.find(o => o.id === a.author_user_id && o.type === 'user') ?? { id: a.author_user_id, name: a.display_name, type: 'user' };
              return { name: a.display_name, type: 'free' };
            })}
            onChange={(_, selected) => {
              const authors = selected.map((v, i) => ({
                display_name: typeof v === 'string' ? v : v.name,
                author_id: typeof v === 'object' && v.type === 'author' ? v.id : null,
                author_user_id: typeof v === 'object' && v.type === 'user' ? v.id : null,
                order: i,
              }));
              setForm(f => ({ ...f, authors }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Author(s)"
                variant="outlined"
                fullWidth
                error={!!errors.author}
                helperText={errors.author || (form.authors.length > 0 ? `${form.authors.length} author${form.authors.length > 1 ? 's' : ''} added` : '')}
              />
            )}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Prep Time"
                variant="outlined"
                fullWidth
                value={form.prep_time}
                onChange={(e) => setForm({ ...form, prep_time: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Recipe Yield"
                variant="outlined"
                fullWidth
                value={form.recipe_yield}
                onChange={(e) => setForm({ ...form, recipe_yield: e.target.value })}
              />
            </Grid>
          </Grid>

          <TextField
            label="Description"
            variant="outlined"
            fullWidth
            multiline
            rows={3}
            value={form.recipe_description}
            onChange={(e) => setForm({ ...form, recipe_description: e.target.value })}
          />

          <FormControl fullWidth error={!!errors.recipe_type}>
            <InputLabel>Recipe Type</InputLabel>
            <Select
              value={form.recipe_type_id}
              onChange={(e) => setForm({ ...form, recipe_type_id: e.target.value })}
              label="Recipe Type"
            >
              <MenuItem value="" disabled>Select Recipe Type</MenuItem>
              {RECIPE_TYPES.map((type) => (
                <MenuItem key={type.id} value={type.id}>{type.label}</MenuItem>
              ))}
            </Select>
            {errors.recipe_type && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.recipe_type}
              </Typography>
            )}
          </FormControl>

          {/* Image upload */}
          <Box
            sx={{
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <input
              accept="image/*"
              id="file-input-edit"
              type="file"
              style={{ display: "none" }}
              onChange={(e) => setUploadedImage(e.target.files[0])}
            />
            {form.uploaded_image ? (
              <>
                <Box
                  component="img"
                  src={previewUrlRef.current}
                  alt="Preview"
                  sx={{ maxHeight: 180, maxWidth: "100%", borderRadius: 1, objectFit: "contain" }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
                    {form.uploaded_image.name}
                  </Typography>
                  <label htmlFor="file-input-edit">
                    <IconButton component="span" size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </label>
                </Box>
              </>
            ) : (
              <>
                <label htmlFor="file-input-edit">
                  <Button variant="outlined" component="span" startIcon={<AddPhotoAlternateIcon />}>
                    Upload New Image
                  </Button>
                </label>
                <Typography variant="caption" color="text.secondary">
                  Current image retained unless you upload a new one.
                </Typography>
              </>
            )}
          </Box>

          {/* Ingredients */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Ingredients</Typography>
              {form.ingredient_categories.length > 1 && (
                <Button size="small" startIcon={<SwapVertIcon />} onClick={() => setReorderModal('ingredients')}>
                  Reorder Sections
                </Button>
              )}
            </Box>
            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleIngredientCategoryDragEnd}>
              <SortableContext items={form.ingredient_categories.map((_, i) => `ing-cat-${i}`)} strategy={verticalListSortingStrategy}>
                {form.ingredient_categories.map((category, catIndex) => (
                  <SortableSection key={`ing-cat-${catIndex}`} id={`ing-cat-${catIndex}`}>
                    <TextField
                      label="Section name (optional)" variant="outlined" fullWidth size="small"
                      value={category.name}
                      onChange={(e) => handleCategoryNameChange(e.target.value, "ingredients", catIndex)}
                      sx={{ mb: 2 }}
                      InputProps={form.ingredient_categories.length > 1 ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => deleteIngredientCategory(catIndex)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      } : undefined}
                    />
                    <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={(e) => handleIngredientDragEnd(catIndex, e)}>
                      <SortableContext items={category.ingredients.map((_, i) => `${catIndex}-ing-${i}`)} strategy={verticalListSortingStrategy}>
                        {category.ingredients.map((ingredientObj, index) => (
                          <SortableIngredientRow
                            key={`${catIndex}-ing-${index}`}
                            id={`${catIndex}-ing-${index}`}
                            ingredientObj={ingredientObj}
                            index={index}
                            catIndex={catIndex}
                            showDelete={category.ingredients.length > 1}
                            errors={errors}
                            onChange={handleIngredientChange}
                            onDelete={deleteIngredient}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                    <Button size="small" onClick={() => addIngredient(catIndex)} sx={{ mt: 0.5 }}>
                      + Add Ingredient
                    </Button>
                  </SortableSection>
                ))}
              </SortableContext>
            </DndContext>
            <Button variant="outlined" size="small" onClick={addIngredientCategory}>
              + Add Section
            </Button>
          </Box>

          {/* Directions */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Directions</Typography>
              {form.direction_categories.length > 1 && (
                <Button size="small" startIcon={<SwapVertIcon />} onClick={() => setReorderModal('directions')}>
                  Reorder Sections
                </Button>
              )}
            </Box>
            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDirectionCategoryDragEnd}>
              <SortableContext items={form.direction_categories.map((_, i) => `dir-cat-${i}`)} strategy={verticalListSortingStrategy}>
                {form.direction_categories.map((category, catIndex) => (
                  <SortableSection key={`dir-cat-${catIndex}`} id={`dir-cat-${catIndex}`}>
                    <TextField
                      label="Section name (optional)" variant="outlined" fullWidth size="small"
                      value={category.name}
                      onChange={(e) => handleCategoryNameChange(e.target.value, "directions", catIndex)}
                      sx={{ mb: 2 }}
                      InputProps={form.direction_categories.length > 1 ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => deleteDirectionCategory(catIndex)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      } : undefined}
                    />
                    <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDirectionDragEnd(catIndex, e)}>
                      <SortableContext items={category.directions.map((_, i) => `${catIndex}-dir-${i}`)} strategy={verticalListSortingStrategy}>
                        {category.directions.map((direction, dIndex) => (
                          <SortableDirectionRow
                            key={`${catIndex}-dir-${dIndex}`}
                            id={`${catIndex}-dir-${dIndex}`}
                            direction={direction}
                            index={dIndex}
                            catIndex={catIndex}
                            showDelete={category.directions.length > 1}
                            errors={errors}
                            onChange={handleDirectionChange}
                            onDelete={deleteDirection}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                    <Button size="small" onClick={() => addDirection(catIndex)} sx={{ mt: 0.5 }}>
                      + Add Step
                    </Button>
                  </SortableSection>
                ))}
              </SortableContext>
            </DndContext>
            <Button variant="outlined" size="small" onClick={addDirectionCategory}>
              + Add Section
            </Button>
          </Box>

          {serverError && <Alert severity="error">{serverError}</Alert>}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={submitting} sx={{ minWidth: 130 }}>
          {submitting ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipeEdit;
